import {
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { NotificationService, notificationErrorMessage } from '../../../../core/services/notification.service';
import { WebPushService } from '../../../../core/services/web-push.service';
import { Invite } from '../../../../core/models/invite.model';
import { ROLE_LABELS } from '../../../../core/models/user.model';
import { formatUserFacingError } from '../../../../core/utils/firebase-error.util';
import {
  InboxEntry,
  NeedsActionItem,
  NotificationSeverity,
  NOTIFICATION_SEVERITY_ICONS,
  NOTIFICATION_SEVERITY_LABELS,
} from '../../../../core/models/notification.model';
import { ButtonComponent } from '../../button/button.component';
import { ButtonGroupComponent, ButtonGroupOption } from '../../forms/button-group/button-group.component';
import { InputComponent } from '../../forms/input/input.component';
import { HeadingComponent } from '../../typography/heading/heading.component';
import { TextComponent } from '../../typography/text/text.component';
import { LucideIconComponent } from '../../lucide-icon/lucide-icon.component';
import { ToastService } from '../../toast/toast.service';
import { TimeAgoPipe } from '../../../pipes/time-ago.pipe';

type PanelTab = 'needs' | 'all';

/**
 * Notifications popover/bottom-sheet content: shared "Needs action" queue + personal "All" inbox.
 * Hosted by NotificationBellComponent inside a CDK overlay.
 */
@Component({
  selector: 'app-notification-panel',
  standalone: true,
  imports: [
    FormsModule,
    ButtonComponent,
    ButtonGroupComponent,
    InputComponent,
    HeadingComponent,
    TextComponent,
    LucideIconComponent,
    TimeAgoPipe,
  ],
  templateUrl: './notification-panel.component.html',
  styleUrl: './notification-panel.component.css',
  host: {
    role: 'dialog',
    'aria-label': 'Notifications',
    '[class.is-mobile]': 'mobile()',
  },
})
export class NotificationPanelComponent {
  protected readonly notifications = inject(NotificationService);
  protected readonly webPush = inject(WebPushService);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);

  readonly mobile = input(false);
  readonly closed = output<void>();

  protected readonly tab = signal<PanelTab>('needs');
  protected readonly severityFilter = signal<NotificationSeverity | null>(null);
  protected readonly busy = signal<ReadonlySet<string>>(new Set());
  protected readonly resolvingId = signal<string | null>(null);
  protected readonly resolveNote = signal('');
  protected readonly markingAll = signal(false);

  protected readonly severityIcons = NOTIFICATION_SEVERITY_ICONS;
  protected readonly severityLabels = NOTIFICATION_SEVERITY_LABELS;
  protected readonly severities: NotificationSeverity[] = ['critical', 'action', 'info'];
  protected readonly skeletonRows = [0, 1, 2, 3];
  protected readonly roleLabels = ROLE_LABELS;

  protected readonly pendingInvites = computed(() => {
    const f = this.severityFilter();
    if (this.tab() === 'needs' && f && f !== 'action') return [];
    return this.notifications.pendingInvites();
  });

  protected readonly tabOptions = computed<ButtonGroupOption[]>(() => {
    const n = this.notifications.needsActionCount();
    const u = this.notifications.unreadCount() + this.notifications.pendingInvites().length;
    return [
      { value: 'needs', label: n ? `Needs action · ${n}` : 'Needs action' },
      { value: 'all', label: u ? `All · ${u}` : 'All' },
    ];
  });

  protected readonly needsAction = computed(() => {
    const f = this.severityFilter();
    const list = this.notifications.needsAction();
    return f ? list.filter((n) => n.severity === f) : list;
  });

  protected readonly inbox = computed(() => this.notifications.inbox());

  // ---- Navigation -----------------------------------------------------------------------

  protected openNeedsAction(item: NeedsActionItem): void {
    if (!item.deepLink) return;
    this.closed.emit();
    void this.router.navigateByUrl(item.deepLink);
  }

  protected openInbox(entry: InboxEntry): void {
    if (!entry.read) {
      this.notifications.markRead(entry.id).catch((e) => this.toast.error(notificationErrorMessage(e)));
    }
    if (entry.deepLink) {
      this.closed.emit();
      void this.router.navigateByUrl(entry.deepLink);
    }
  }

  protected goToPreferences(): void {
    this.closed.emit();
    void this.router.navigate(['/settings'], { queryParams: { tab: 'notifications' } });
  }

  protected viewAll(): void {
    this.closed.emit();
    void this.router.navigateByUrl('/notifications');
  }

  // ---- Actions --------------------------------------------------------------------------

  protected toggleSeverity(s: NotificationSeverity): void {
    this.severityFilter.update((cur) => (cur === s ? null : s));
  }

  protected async claim(item: NeedsActionItem, release = false): Promise<void> {
    await this.withBusy(item.id, () => this.notifications.claim(item.id, release));
  }

  protected startResolve(item: NeedsActionItem): void {
    this.resolvingId.set(item.id);
    this.resolveNote.set('');
  }

  protected cancelResolve(): void {
    this.resolvingId.set(null);
    this.resolveNote.set('');
  }

  protected async confirmResolve(item: NeedsActionItem): Promise<void> {
    const note = this.resolveNote();
    const ok = await this.withBusy(item.id, () => this.notifications.resolve(item.id, note));
    if (ok) {
      this.cancelResolve();
      this.toast.success('Marked as resolved');
    }
  }

  protected async markAllRead(): Promise<void> {
    this.markingAll.set(true);
    try {
      await this.notifications.markAllRead();
    } catch (e) {
      this.toast.error(notificationErrorMessage(e));
    } finally {
      this.markingAll.set(false);
    }
  }

  protected async enablePush(): Promise<void> {
    await this.webPush.enable();
  }

  protected async acceptInvite(invite: Invite): Promise<void> {
    if (!invite.id) return;
    const key = `invite:${invite.id}`;
    this.busy.update((s) => new Set(s).add(key));
    try {
      await this.notifications.acceptInvite(invite);
      this.toast.success(`Joined ${invite.companyName}!`);
    } catch (e: unknown) {
      this.toast.error(formatUserFacingError(e, 'Failed to accept invitation.'));
    } finally {
      this.busy.update((s) => {
        const next = new Set(s);
        next.delete(key);
        return next;
      });
    }
  }

  protected async declineInvite(invite: Invite): Promise<void> {
    if (!invite.id) return;
    const key = `invite:${invite.id}`;
    this.busy.update((s) => new Set(s).add(key));
    try {
      await this.notifications.declineInvite(invite);
      this.toast.success('Invitation declined.');
    } catch (e: unknown) {
      this.toast.error(formatUserFacingError(e, 'Failed to decline invitation.'));
    } finally {
      this.busy.update((s) => {
        const next = new Set(s);
        next.delete(key);
        return next;
      });
    }
  }

  protected formatInviteRoles(invite: Invite): string {
    return (invite.roles || []).map((r) => this.roleLabels[r] || r).join(', ');
  }

  protected isBusy(id: string): boolean {
    return this.busy().has(id);
  }

  protected chipLabel(args: Record<string, string | number> | undefined, keys: string[]): string | null {
    if (!args) return null;
    for (const k of keys) {
      const v = args[k];
      if (v !== undefined && v !== null && v !== '') return String(v);
    }
    return null;
  }

  protected absoluteTime(ms: number | null | undefined): string {
    return ms ? new Date(ms).toLocaleString() : '';
  }

  private async withBusy(id: string, fn: () => Promise<void>): Promise<boolean> {
    this.busy.update((s) => new Set(s).add(id));
    try {
      await fn();
      return true;
    } catch (e) {
      this.toast.error(notificationErrorMessage(e));
      return false;
    } finally {
      this.busy.update((s) => {
        const next = new Set(s);
        next.delete(id);
        return next;
      });
    }
  }
}
